import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { useApiClientsOrNull, MessageAtom } from 'design-system/my-lib';
import type { AppDispatch } from '@center-pwa/store/store';
import { cloneContextThunk } from '@center-pwa/reducers/contextsReducer';
import ContextListPage from '../ContextsPage/ContextListPage';
import './StudioContextGalleryPage.scss';

/** Mirrors the retired `NewContextPage.TEMPLATE_SYSTEM_NAMES` — the four built-in system
 * contexts, foldable into the "New…" menu's "From built-in" submenu (W11). */
const BUILT_IN_TEMPLATES: readonly { id: string; systemName: string }[] = [
  { id: 'pri', systemName: 'center_priorities_strategies' },
  { id: 'rep', systemName: 'center_reporting_chain' },
  { id: 'team', systemName: 'center_team_workload' },
  { id: 'okr', systemName: 'center_okr_tree' },
];

/** `system_name`-only address (contexts have no template-id-keyed route) — mirrors
 * `ROUTE_BUILDERS_BY_TYPE.context` in `studioRouteForTemplate.ts`. */
const contextEditPath = (systemName: string): string => `/studio/context/${systemName}`;

/**
 * `/studio/context` (EAINT-862) — re-parents `ContextListPage` under Studio (chrome
 * suppressed) and replaces `NewContextPage`'s standalone route with an inline "New…" menu
 * (Blank | From built-in → clone) per the transcribed W11 IA. Distribution chips and the
 * visually-separated "Built-ins" section are Phase 4 work (they need the batched channel
 * lookup this phase doesn't wire) — `ContextInventoryOrganism` already badges/locks built-in
 * cards today via `classifyContextSource`, so re-parenting loses no existing capability.
 */
const StudioContextGalleryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const logAnalyticsEvent = useAnalytics();
  const apiClients = useApiClientsOrNull();
  const menuRef = useRef<Menu>(null);

  const [cloningTemplateId, setCloningTemplateId] = useState<string | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);

  const handleBlank = useCallback(() => {
    navigate('/studio/context/new');
  }, [navigate]);

  const handleCloneBuiltIn = useCallback(
    async (builtInSystemName: string, templateId: string) => {
      if (!apiClients?.schemaClient) return;
      setCloningTemplateId(templateId);
      setCloneError(null);
      try {
        const cloned = await dispatch(
          cloneContextThunk({
            apiClient: apiClients.schemaClient,
            systemName: builtInSystemName,
            payload: {
              newName: { default: t(`context.newContext.templates.${templateId}.name`) },
            },
          })
        ).unwrap();
        logAnalyticsEvent('context_created', cloned.system_name, {
          entryPath: 'template',
          templateRef: builtInSystemName,
        });
        navigate(contextEditPath(cloned.system_name));
      } catch (err: unknown) {
        // The raw message is developer-facing English (axios/network text) — it is logged,
        // never rendered. The user sees the localized failure string.
        console.error('[StudioContextGalleryPage] Failed to clone built-in context', err);
        setCloneError(t('context.newContext.cloneError'));
      } finally {
        setCloningTemplateId(null);
      }
    },
    [apiClients, dispatch, navigate, logAnalyticsEvent, t]
  );

  const menuItems: MenuItem[] = [
    {
      label: t('studio.context.gallery.newMenu.blank'),
      icon: 'pi pi-file',
      command: handleBlank,
    },
    {
      label: t('studio.context.gallery.newMenu.fromBuiltIn'),
      icon: 'pi pi-copy',
      items: BUILT_IN_TEMPLATES.map((tmpl) => ({
        label: t(`context.newContext.templates.${tmpl.id}.name`),
        disabled: !!cloningTemplateId,
        command: () => handleCloneBuiltIn(tmpl.systemName, tmpl.id),
      })),
    },
  ];

  const handleOpenNewMenu = useCallback((event?: unknown) => {
    menuRef.current?.toggle(event as React.SyntheticEvent);
  }, []);

  return (
    <div className="studio-context-gallery-page" data-testid="studio-context-gallery-page">
      <Menu model={menuItems} popup ref={menuRef} data-testid="studio-context-new-menu" />
      {cloneError && (
        <MessageAtom
          severity="error"
          text={cloneError}
          data-testid="studio-context-gallery-clone-error"
        />
      )}
      <ContextListPage hideChrome editPath={contextEditPath} onNewClick={handleOpenNewMenu} />
    </div>
  );
};

export default StudioContextGalleryPage;
